import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, Button, IconButton, MenuItem, Select, TextField, Tooltip } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckIcon from '@mui/icons-material/Check'
import { tone, ui } from '../../../../../../utils/uiTokens'
import { ImportQuestion, issueOf, MAX_MULTI } from './types'

interface Props {
    q: ImportQuestion
    n: number
    editing: boolean
    locked: boolean
    onEdit: () => void
    onSave: (q: ImportQuestion) => void
    onCancel: () => void
    onChange: (q: ImportQuestion) => void
}

const badge = (t: { text: string; bg: string; border: string }) => ({
    fontSize: 12,
    fontWeight: 600,
    color: t.text,
    background: t.bg,
    border: `1px solid ${t.border}`,
    borderRadius: '8px',
    px: 1,
    py: '2px',
})

// Нажатие на букву: single - одна, multi - до MAX_MULTI (четвёртую не даём отметить)
export const pick = (q: ImportQuestion, k: string): ImportQuestion => {
    if (q.type === 'match') return q
    const cur = q.correct as string[]
    if (q.type === 'single') return { ...q, correct: [k], ai: false }
    if (cur.includes(k)) return { ...q, correct: cur.filter((x) => x !== k), ai: false }
    if (cur.length >= MAX_MULTI) return q
    return { ...q, correct: [...cur, k].sort(), ai: false }
}

const QuestionCard: React.FC<Props> = ({ q, n, editing, locked, onEdit, onSave, onCancel, onChange }) => {
    const { formatMessage } = useIntl()
    const [draft, setDraft] = useState<ImportQuestion>(q)
    const cur = editing ? draft : q
    const set = editing ? setDraft : onChange
    const issue = issueOf(cur)
    const rule =
        q.type === 'single' ? 'qiRuleSingle' : q.type === 'multi' ? 'qiRuleMulti' : 'qiRuleMatch'
    const typeTone = q.type === 'single' ? tone.info : q.type === 'multi' ? tone.warn : tone.ok
    const correct = (k: string) => q.type !== 'match' && (cur.correct as string[]).includes(k)

    const setOpt = (i: number, v: string) =>
        set({ ...cur, opts: cur.opts.map((o, j) => (j === i ? [o[0], v] : o)) as ImportQuestion['opts'] })
    const setSub = (i: number, v: string) =>
        set({ ...cur, subs: cur.subs!.map((s, j) => (j === i ? [s[0], v] : s)) as ImportQuestion['subs'] })
    const setMatch = (sub: string, opt: string) =>
        set({ ...cur, correct: { ...(cur.correct as Record<string, string>), [sub]: opt }, ai: false })

    return (
        <Box
            sx={{
                border: `1px solid ${editing ? ui.pink : issue ? tone.warn.border : ui.border}`,
                background: issue && !editing ? tone.yellow.bg : ui.card,
                borderRadius: ui.radius,
                p: 2,
                mb: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Box sx={{ fontWeight: 700, color: ui.ink }}>{formatMessage({ id: 'qiQuestionN' }, { n })}</Box>
                <Box sx={badge(typeTone)}>
                    {formatMessage({ id: q.type === 'single' ? 'qiFilterSingle' : q.type === 'multi' ? 'qiFilterMulti' : 'qiFilterMatch' })}
                </Box>
                <Box sx={{ color: ui.sub, fontSize: 13 }}>{formatMessage({ id: rule })}</Box>
                {q.hasImage && <Box sx={badge(tone.neutral)}>{formatMessage({ id: 'qiImage' })}</Box>}
                {cur.ai && !issue && <Box sx={badge(tone.yellow)}>{formatMessage({ id: 'qiAiHint' })}</Box>}
                {editing && <Box sx={badge({ text: ui.pink, bg: ui.pinkSoft, border: ui.pink })}>{formatMessage({ id: 'qiEditMode' })}</Box>}
                <Box sx={{ flex: 1 }} />
                {!editing && (
                    <Tooltip title={formatMessage({ id: 'qiEditTip' })}>
                        <span>
                            <IconButton size="small" disabled={locked} onClick={() => { setDraft(q); onEdit() }}>
                                <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                    {editing ? (
                        <TextField
                            multiline
                            fullWidth
                            size="small"
                            value={cur.text}
                            onChange={(e) => set({ ...cur, text: e.target.value })}
                        />
                    ) : (
                        <Box sx={{ color: ui.ink, whiteSpace: 'pre-wrap' }}>{q.text}</Box>
                    )}
                    {q.hasImage && q.imageNote && (
                        <Box sx={{ color: ui.muted, fontSize: 13, mt: 1 }}>{q.imageNote}</Box>
                    )}
                </Box>

                <Box>
                    {cur.opts.map((o, i) => (
                        <Box key={o[0]} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <IconButton
                                size="small"
                                disabled={q.type === 'match' || (locked && !editing)}
                                onClick={() => set(pick(cur, o[0]))}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    border: `1px solid ${correct(o[0]) ? ui.green : ui.border}`,
                                    background: correct(o[0]) ? ui.green : ui.surface,
                                    color: correct(o[0]) ? '#fff' : ui.sub,
                                    '&:hover': { background: correct(o[0]) ? ui.green : ui.track },
                                }}
                            >
                                {correct(o[0]) ? <CheckIcon sx={{ fontSize: 16 }} /> : o[0]}
                            </IconButton>
                            {editing ? (
                                <TextField fullWidth size="small" value={o[1]} onChange={(e) => setOpt(i, e.target.value)} />
                            ) : (
                                <Box sx={{ color: ui.ink }}>{o[1]}</Box>
                            )}
                        </Box>
                    ))}

                    {q.type === 'match' &&
                        cur.subs!.map((s, i) => (
                            <Box key={s[0]} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                                {editing ? (
                                    <TextField size="small" sx={{ flex: 1, minWidth: 140 }} value={s[1]} onChange={(e) => setSub(i, e.target.value)} />
                                ) : (
                                    <Box sx={{ flex: 1, minWidth: 140, color: ui.ink }}>{s[1]}</Box>
                                )}
                                <Select
                                    size="small"
                                    displayEmpty
                                    disabled={locked && !editing}
                                    sx={{ flex: 1, minWidth: 160 }}
                                    value={(cur.correct as Record<string, string>)[s[0]] || ''}
                                    onChange={(e) => setMatch(s[0], String(e.target.value))}
                                >
                                    <MenuItem value="" disabled>
                                        {formatMessage({ id: 'qiPickMatch' })}
                                    </MenuItem>
                                    {cur.opts.map((o) => (
                                        <MenuItem key={o[0]} value={o[0]}>{`${o[0]}) ${o[1]}`}</MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        ))}
                </Box>
            </Box>

            {issue && <Box sx={{ color: tone.warn.text, fontSize: 13, mt: 1.5 }}>{formatMessage({ id: issue })}</Box>}

            {editing && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button variant="outlined" onClick={onCancel}>
                        {formatMessage({ id: 'cancel' })}
                    </Button>
                    <Button variant="contained" disabled={!!issue || !cur.text.trim()} onClick={() => onSave(cur)}>
                        {formatMessage({ id: 'save' })}
                    </Button>
                </Box>
            )}
        </Box>
    )
}

export default QuestionCard
